import { FormPack, ElementPack, PagePack, Element, Form, Page } from "../../models";
import { FormNode, PageNode } from "../containerNodes";
import { ElementNode, CheckboxNode, DateNode, DropdownElementNode, DropdownGroupNode, RadioButtonElementNode, RadioButtonGroupNode, RowNode, SectionNode, TableNode, CellNode } from "..";
import { ElementAttributeType, ElementType, FormAttributeType } from "../../enums";
import { InvalidNode, LabelNode, ImageNode } from "../staticNodes";
import { TextBoxNode } from "../inputNodes";
import { InputNode } from "../primitiveNodes";
import { IStyle, ITextStyle, Color } from "../styling";

const getValue = (elem: Element, attr: ElementAttributeType) => {
    let tuple = elem.attributes.filter(v => v.attributeType === attr).pop();

    if (tuple === undefined) {
        return undefined;
    }

    return tuple.value;
};

const formToNode = (form: Form): FormNode => {
    const node = new FormNode();

    node.clientId = form.bloomClientId;
    node.formType = form.formTypeId;
    node.name = form.name;
    node.primId = form.id;
    node.children = [];

    return node;
};

const pageToNode = (page: Page): PageNode => {
    const node = new PageNode();
    
    node.name = page.name;
    node.primId = page.id;
    node.children = [];

    return node;
};

const elementToNode = (elem: Element): ElementNode => {
    let node = new InvalidNode();

    const getAttr = (type: ElementAttributeType) => getValue(elem, type);
    const getIntAttr = (type: ElementAttributeType): number | undefined => {
        const val = getAttr(type);

        if (val === undefined) {
            return undefined;
        }

        return +val;
    };
    const getColor = (type: ElementAttributeType): Color | undefined => (
        (hex: string | undefined) => hex !== undefined ? Color.fromHex(hex) : undefined
    )(getAttr(type));

    const unpackInput = (inNode: InputNode<any>) => {
        inNode.isDisabled = getAttr(ElementAttributeType.IsDisabled) === "true";
        inNode.placeholder = getAttr(ElementAttributeType.Placeholder);
        inNode.requiredMessage = getAttr(ElementAttributeType.RequiredMessage);

        return inNode;
    }

    const val = getAttr(ElementAttributeType.DefaultValue);
    switch (elem.elementType) {
        case ElementType.Checkbox:
        const cbNode = new CheckboxNode();
        cbNode.input = val === undefined ? undefined : val === "true";

        node = unpackInput(cbNode);
        break;

        case ElementType.Date:
        const dateNode = new DateNode();
        dateNode.input = val === undefined ? undefined : new Date(val);
        dateNode.pastDateMessage = getAttr(ElementAttributeType.PastDateMessage);
        dateNode.futureDateMessage = getAttr(ElementAttributeType.FutureDateMessage);
        dateNode.invalidDateMessage = getAttr(ElementAttributeType.Invalid);

        node = unpackInput(dateNode);
        break;

        case ElementType.DropdownElement:
        node = new DropdownElementNode();
        break;

        case ElementType.DropdownGroup:
        const ddgNode = new DropdownGroupNode();
        ddgNode.input = val;

        node = unpackInput(ddgNode);
        break;

        case ElementType.RadioButtonElement:
        const radioNode = new RadioButtonElementNode();
        radioNode.input = val === undefined ? undefined : val === "true";
        node = unpackInput(radioNode);
        break;

        case ElementType.RadioButtonGroup:
        node = new RadioButtonGroupNode();
        break;

        case ElementType.Row:
        node = new RowNode();
        break;

        case ElementType.Section:
        node = new SectionNode();
        break;

        case ElementType.Table:
        node = new TableNode();
        break;

        case ElementType.Textbox:
        const tbNode = new TextBoxNode();
        tbNode.input = val;
        tbNode.invalidZipCodeMessage = getAttr(ElementAttributeType.InvalidZipCodeMessage);

        node = unpackInput(tbNode);
        break;

        case ElementType.VariableText:
        node = new LabelNode();
        break;

        case ElementType.Cell:
        node = new CellNode();
        break;

        case ElementType.Image:
        node = new ImageNode();
        break;

        default:
        return new InvalidNode();
    }

    node.elementType = elem.elementType;
    node.formContextId = elem.formContextId;
    node.formInputName = elem.formInputName;
    node.isHidden = getAttr(ElementAttributeType.IsHidden) === "true";
    node.isMultiline = getAttr(ElementAttributeType.IsMultiline) === "true";
    node.maxLength = (v => v === undefined ? undefined : Number.parseInt(v))(getAttr(ElementAttributeType.MaxLength));
    node.parentFormContextId = elem.parentFormContextId;
    node.prototypeId = elem.prototypeId;
    node.summaryValue = getAttr(ElementAttributeType.SummaryValue);
    node.styleName = getAttr(ElementAttributeType.Style);
    node.textStyleName = getAttr(ElementAttributeType.TextStyle);
    node.text = getAttr(ElementAttributeType.Text);
    node.dynamicName = elem.dynamicName;
    node.url = getAttr(ElementAttributeType.URL);
    node.dateFormat = getAttr(ElementAttributeType.DateFormat);
    node.children = [];

    const style = node.style;
    const dim = style.dimensions;
    const border = style.border;
    const radius = border.radii;
    const borderWidth = border.width;
    const padding = style.padding;

    dim.height = getIntAttr(ElementAttributeType.Height);
    dim.width = getIntAttr(ElementAttributeType.Width);

    radius.topLeft = getIntAttr(ElementAttributeType.BorderRadiusTopLeft);
    radius.topRight = getIntAttr(ElementAttributeType.BorderRadiusTopRight);
    radius.bottomRight = getIntAttr(ElementAttributeType.BorderRadiusBottomRight);
    radius.bottomLeft = getIntAttr(ElementAttributeType.BorderRadiusBottomLeft);

    borderWidth.left = getIntAttr(ElementAttributeType.BorderLeft);
    borderWidth.top = getIntAttr(ElementAttributeType.BorderTop);
    borderWidth.right = getIntAttr(ElementAttributeType.BorderRight);
    borderWidth.bottom = getIntAttr(ElementAttributeType.BorderBottom);

    border.color = getColor(ElementAttributeType.BorderColor);

    padding.left = getIntAttr(ElementAttributeType.PaddingLeft);
    padding.top = getIntAttr(ElementAttributeType.PaddingTop);
    padding.right = getIntAttr(ElementAttributeType.PaddingRight);
    padding.bottom = getIntAttr(ElementAttributeType.PaddingBottom);

    style.backgroundColor = getColor(ElementAttributeType.BackgroundColor);
    style.horizontalAlignment = getAttr(ElementAttributeType.HorizontalAlignment);
    style.className = getAttr(ElementAttributeType.Style);

    return node;
};

export const unpackElement = (pack: ElementPack): ElementNode => {
    const node = elementToNode(pack.item);
    node.children = pack.children.map(unpackElement);

    return node;
};

export const unpackPage = (pack: PagePack): PageNode => {
    const node = pageToNode(pack.item);
    node.children = pack.children.map(unpackElement)

    return node;
};

export const unpackForm = (pack: FormPack): FormNode => {
    const node = formToNode(pack.item);
    node.children = pack.children.map(unpackPage);

    return node;
};

export const createPageTree = (page: Page): PageNode => {
    const pageNode = pageToNode(page);
    const elementMap: { [id: number]: ElementNode } = {};
    const nodeCollection = page.elements.map(elementToNode);

    nodeCollection.forEach(v => {
        if (v.formContextId === undefined) {
            return;
        }

        elementMap[v.formContextId] = v;
    });

    nodeCollection.forEach(v => {
        if (v.parentFormContextId === null || v.parentFormContextId === undefined) {
            pageNode.children.push(v);
        } else if (elementMap[v.parentFormContextId] !== undefined) {
            elementMap[v.parentFormContextId].children.push(v);
        }
    });

    return pageNode;
}

export const createFormTree = (form: Form): FormNode => {
    const node = formToNode(form);
    node.children = form.pages.map(createPageTree);

    return node;
}
